import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

const parseNumber = (value: string | undefined, fallback: number) => {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function setupHomeMotion(root: HTMLElement) {
  const reducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(max-width: 767.98px)').matches;
  const splitTextInstances: SplitText[] = [];
  const restoredMarkup = new Map<HTMLElement, string>();
  const eventCleanups: Array<() => void> = [];

  const build = () =>
    gsap.context(() => {
      if (reducedMotion) {
        gsap.set(
          root.querySelectorAll(
            '.at_fade_anim, .at-rise-animation, .at-char-animation, .reveal-text, .anim-zoomin'
          ),
          { clearProps: 'all', opacity: 1 }
        );
        return;
      }

      root.querySelectorAll<HTMLElement>('.at_fade_anim').forEach((item) => {
        const direction = item.dataset.fadeFrom ?? 'bottom';
        const offset = parseNumber(item.dataset.fadeOffset, 20);
        const onScroll =
          Number.parseInt(item.dataset.onScroll ?? '1', 10) !== 0;
        const tween = gsap.fromTo(
          item,
          {
            opacity: 0,
            x:
              direction === 'left'
                ? -offset
                : direction === 'right'
                  ? offset
                  : 0,
            y:
              direction === 'top'
                ? -offset
                : direction === 'bottom'
                  ? offset
                  : 0,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            ease: item.dataset.ease ?? 'power2.out',
            duration: parseNumber(item.dataset.duration, 0.75),
            delay: parseNumber(item.dataset.delay, 0.15),
            paused: onScroll,
          }
        );

        if (onScroll) {
          ScrollTrigger.create({
            trigger: item,
            start: `top ${item.dataset.start ?? '85%'}`,
            once: true,
            onEnter: () => tween.play(),
          });
        }
      });

      root.querySelectorAll<HTMLElement>('.reveal-text').forEach((item) => {
        const split = new SplitText(item, {
          type: 'lines,words,chars',
          linesClass: 'split-line',
        });
        splitTextInstances.push(split);
        if (!split.chars.length) return;
        gsap.set(split.chars, { opacity: 0.4, x: -7 });

        if (item.classList.contains('reveal-text--prompt')) {
          gsap.to(split.chars, {
            x: 0,
            y: 0,
            opacity: 1,
            duration: 0.45,
            stagger: 0.004,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: item,
              start: 'top 94%',
              once: true,
            },
          });
          return;
        }

        gsap.to(split.chars, {
          x: 0,
          y: 0,
          opacity: 1,
          scrollTrigger: {
            trigger: item,
            start: 'top 92%',
            end: 'top 70%',
            scrub: 0.6,
          },
        });
      });

      root
        .querySelectorAll<HTMLElement>('.at-rise-animation')
        .forEach((item) => {
          const split = new SplitText(item, {
            type: 'lines,words,chars',
            linesClass: 'split-line',
          });
          splitTextInstances.push(split);
          if (!split.chars.length) return;
          gsap.set(split.chars, { yPercent: 110, willChange: 'transform' });
          gsap.to(split.chars, {
            yPercent: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.03,
            onComplete: () => {
              split.lines.forEach((line) => {
                (line as HTMLElement).style.overflow = 'visible';
              });
            },
            scrollTrigger: {
              trigger: item,
              start: 'top 85%',
              once: true,
            },
          });
        });

      root
        .querySelectorAll<HTMLElement>(
          '.ship-smart-hero .sec-1-home-12__title, .ship-smart-hero .sec-1-home-12__sub'
        )
        .forEach((item) => {
          gsap.fromTo(
            item,
            { opacity: 1 },
            {
              opacity: 0,
              ease: 'none',
              immediateRender: false,
              scrollTrigger: {
                trigger: item,
                start: 'top 12%',
                end: 'top top',
                scrub: 0.35,
              },
            }
          );
        });

      root
        .querySelectorAll<HTMLElement>('.at-char-animation')
        .forEach((item) => {
          const split = new SplitText(item, { type: 'chars,words' });
          splitTextInstances.push(split);
          if (!split.chars.length) return;
          gsap.set(item, { perspective: 300 });
          gsap.from(split.chars, {
            x: 100,
            autoAlpha: 0,
            duration: 1,
            delay: 0.5,
            stagger: 0.05,
            scrollTrigger: {
              trigger: item,
              start: 'top 90%',
              end: 'bottom 60%',
              once: true,
            },
          });
        });

      root.querySelectorAll<HTMLElement>('.anim-zoomin').forEach((item) => {
        gsap.from(item, {
          autoAlpha: 0,
          scale: 1.2,
          duration: 2,
          ease: 'power2.out',
          clearProps: 'all',
          scrollTrigger: {
            trigger: item.parentElement ?? item,
            start: 'top 100%',
            once: true,
          },
        });
      });

      root
        .querySelectorAll<HTMLElement>('.scale-img-from-to')
        .forEach((item) => {
          const fromScale = Math.max(
            window.innerWidth < 1200 ? 0.95 : 0,
            parseNumber(item.dataset.value1, 1)
          );
          gsap.fromTo(
            item,
            { scale: fromScale },
            {
              scale: parseNumber(item.dataset.value2, 1),
              ease: 'sine.inOut',
              scrollTrigger: { trigger: item, scrub: true },
            }
          );
        });

      root
        .querySelectorAll<HTMLElement>('.text-scale-anim')
        .forEach((heading) => {
          restoredMarkup.set(heading, heading.innerHTML);
          const rebuiltNodes: Node[] = [];
          Array.from(heading.childNodes).forEach((node) => {
            if (node.nodeType !== Node.TEXT_NODE) {
              rebuiltNodes.push(node.cloneNode(true));
              return;
            }
            const words = (node.textContent ?? '').split(' ');
            words.forEach((word, wordIndex) => {
              const wordSpan = document.createElement('span');
              wordSpan.className = 'at-word-span';
              Array.from(word).forEach((letter) => {
                const letterSpan = document.createElement('span');
                letterSpan.className = 'at-letter-span';
                letterSpan.textContent = letter;
                wordSpan.appendChild(letterSpan);
              });
              rebuiltNodes.push(wordSpan);
              if (wordIndex < words.length - 1) {
                rebuiltNodes.push(document.createTextNode(' '));
              }
            });
          });
          heading.replaceChildren(...rebuiltNodes);

          const letters = Array.from(
            heading.querySelectorAll<HTMLElement>('.at-letter-span')
          );
          letters.forEach((letter, index) => {
            const animate = (entering: boolean) => {
              gsap.to(letter, {
                scaleY: entering ? 1.6 : 1,
                y: entering ? '-24%' : '0%',
                duration: 0.4,
                ease: 'sine.inOut',
                overwrite: true,
              });
              [letters[index - 1], letters[index + 1]].forEach((neighbor) => {
                if (!neighbor) return;
                gsap.to(neighbor, {
                  scaleY: entering ? 1.3 : 1,
                  y: entering ? '-12%' : '0%',
                  duration: 0.4,
                  ease: 'sine.inOut',
                  overwrite: true,
                });
              });
            };
            const enter = () => animate(true);
            const leave = () => animate(false);
            letter.addEventListener('mouseenter', enter);
            letter.addEventListener('mouseleave', leave);
            eventCleanups.push(() => {
              letter.removeEventListener('mouseenter', enter);
              letter.removeEventListener('mouseleave', leave);
            });
          });
        });

      const media = gsap.matchMedia();
      media.add('(min-width: 992px)', () => {
        root
          .querySelectorAll<HTMLElement>('.scroll-move-up')
          .forEach((item) => {
            gsap.to(item, {
              y: -100,
              duration: 1.5,
              scrollTrigger: { trigger: item, start: 'top 70%', scrub: 1 },
            });
          });
      });
      media.add('(min-width: 768px)', () => {
        root
          .querySelectorAll<HTMLElement>('.scroll-move-up2')
          .forEach((item) => {
            gsap.from(item, {
              y: 80,
              opacity: 0,
              duration: 1.5,
              ease: 'power4.out',
              scrollTrigger: {
                trigger: item,
                start: 'top bottom',
                end: 'bottom bottom',
                scrub: 1,
              },
            });
          });
      });

      const journeyWrap = root.querySelector<HTMLElement>('.journey-list-wrap');
      const journeyLine = root.querySelector<HTMLElement>('.journey-list-line');
      if (journeyWrap && journeyLine) {
        gsap.fromTo(
          journeyLine,
          { height: 0 },
          {
            height: '100%',
            ease: 'none',
            scrollTrigger: {
              trigger: journeyWrap,
              start: 'top 50%',
              end: 'top top',
              scrub: 1.2,
            },
          }
        );
      }

      root.querySelectorAll<HTMLElement>('.at-image-hover').forEach((card) => {
        const image = card.querySelector<HTMLElement>('img');
        if (!image) return;
        const enter = () =>
          gsap.to(image, {
            scale: 1.08,
            filter: 'blur(0px)',
            duration: 0.6,
            ease: 'power2.out',
          });
        const leave = () =>
          gsap.to(image, {
            scale: 1,
            filter: 'blur(0px)',
            duration: 0.6,
            ease: 'power2.out',
          });
        card.addEventListener('mouseenter', enter);
        card.addEventListener('mouseleave', leave);
        eventCleanups.push(() => {
          card.removeEventListener('mouseenter', enter);
          card.removeEventListener('mouseleave', leave);
        });
      });

      if (!reducedMotion) {
        root
          .querySelectorAll<HTMLElement>('.scroll-section')
          .forEach((section) => {
            if (section.closest('.section-fix')) return;
            const wrapper = section.querySelector<HTMLElement>('.wrapper');
            const items = wrapper
              ? Array.from(wrapper.querySelectorAll<HTMLElement>('.item'))
              : [];
            if (!items.length) return;

            const direction = section.classList.contains('horizontal-section')
              ? 'horizontal'
              : 'vertical';

            items.forEach((item, index) => {
              gsap.set(item, { zIndex: index });
              if (index !== 0) {
                direction === 'horizontal'
                  ? gsap.set(item, { xPercent: 100 })
                  : gsap.set(item, { yPercent: 100 });
              }
            });

            const timeline = gsap.timeline({
              scrollTrigger: {
                trigger: section,
                pin: true,
                start: 'top top',
                end: () => `+=${items.length * 50}%`,
                scrub: 1,
                invalidateOnRefresh: true,
              },
              defaults: { ease: 'none' },
            });

            items.forEach((item, index) => {
              timeline.to(item, { scale: 0.9 });
              if (items[index + 1]) {
                direction === 'horizontal'
                  ? timeline.to(items[index + 1], { xPercent: 0 }, '<')
                  : timeline.to(items[index + 1], { yPercent: 0 }, '<');
              }
            });
          });
      }
    }, root);

  /* SplitText freezes the line breaks it measures. Starting it against fallback-font
     metrics makes the page reflow when the real font arrives and produces measurable CLS
     on a first visit. `document.fonts.ready` resolves after loading succeeds or fails, so
     there is no need for a timeout that can race the final font metrics. */
  let context: gsap.Context | null = null;
  let cancelled = false;
  const start = () => {
    if (cancelled || context) return;
    context = build();
  };

  const fonts = document.fonts;
  if (!fonts || fonts.status === 'loaded') {
    start();
  } else {
    void fonts.ready.then(start);
  }

  return () => {
    cancelled = true;
    eventCleanups.forEach((cleanup) => cleanup());
    context?.revert();
    splitTextInstances.forEach((split) => split.revert());
    restoredMarkup.forEach((markup, element) => {
      element.innerHTML = markup;
    });
  };
}
